import { formatMoney } from "../../toll/money";
import ShareCampaign from '../../components/ShareCampaign';
import { campaignEarnedMedal } from '../../toll/campaignRules';
import { useEffect } from "react";
import { MyColor } from "../../config/myStyle";
import { goal_coin_big } from "../../imgs";
import { campaignStore, campaignThemeStore, donationStore, userStore } from "../../stores";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import PageBox from "../../layout/PageBox";
import CampaignDeadline from "../../components/CampaignDeadline";
import { useTranslation } from "react-i18next";
import { getCampaignThemeImages } from "../../toll/campaignThemeVisual";

const MyRecordDetail = observer(() => {
  const { t } = useTranslation();
  const { campaignId } = useParams();

  const campaign = campaignStore.campaignMap.get(campaignId ?? '');
  const themeLocale = userStore.language ?? "zh-HK";
  const themeName = campaignThemeStore.campaignThemeDataList?.items?.find(item => item.id === campaign?.campaignThemeId)?.nameObj?.[themeLocale]
    ?? campaignThemeStore.campaignThemeDataList?.items?.find(item => item.id === campaign?.campaignThemeId)?.name;
  const themeImages = getCampaignThemeImages(campaign, themeName);
  const ratioAmount = ((campaign?.raisedAmount ?? 0) / (campaign?.goalAmount ?? 0) * 100).toFixed(0);
  const campaignStatus = campaignStore.campaignStatusMap.get(campaignId ?? '');
  const donationList = donationStore.donationList;

  const isAmountHidden = (item: { isAmountHidden?: boolean; remark?: string | null }) => {
    // 新接口字段优先决定金额是否隐藏，remark 只保留给历史数据兜底使用。
    if (typeof item.isAmountHidden === "boolean") {
      return item.isAmountHidden;
    }
    if (!item.remark) return false;
    try {
      return JSON.parse(item.remark).amountVisibility === "hidden";
    } catch {
      return false;
    }
  };

  const getSupporterDisplayName = (item: typeof donationList[number]) => {
    if (item.isAnonymous) {
      return t('campaign_detail.anonymous');
    }
    // 支持者姓名按捐款接口 donorName 展示，不再拼 firstName 与 lastName。
    return item.donorName?.trim() || t('campaign_detail.anonymous');
  };

  const user = userStore.userDataMap[campaign?.userId || ''];
  const userStatus = userStore.userStatusMap[campaign?.userId || ''];
  useEffect(() => {
    campaignStore.getCampaign(campaignId ?? '');
    donationStore.getDonationList({ campaignId, limit: 1000 });
  }, [campaignId]);

  useEffect(() => {
    if (campaign) {
      userStore.getUserMap({ id: campaign.userId });
    }
  }, [campaign]);
  return (
    <PageBox
      spinning={campaignStatus?.loading || donationStore.donationListStatus.loading || userStatus?.loading}
    >
      <div
        className="w-full"
        style={{
          overflow: "hidden",
          marginTop: 10,
        }}
      >
        {/* 图片区 */}
        <img
          src={themeImages.cover}
          style={{
            width: "100%",
            aspectRatio: "16/9",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
          alt=""
        />

        {/* 内容区 */}
        <div className="flex-col">
          {/* 标题 */}
          <div className="bg-[white] px-[20px] pb-[14px] pt-[14px]" style={{
            borderBottomWidth: 4,
            borderColor: MyColor.sub,
          }}>
            <div
              style={{
                color: "#164194",
                fontWeight: 700,
                fontSize: 22,
                textWrap: 'wrap',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              {campaign?.title}
            </div>
            {/* 描述 */}
            <div
              className="text-[13px] text-[rgba(0,0,0,4)] mt-[6px]"
              style={{
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {`${t("my_record_detail.by")} ${user?.englishGivenName ?? ''} ${user?.englishSurname ?? ''} `}
            </div>
            {/* 进度条 */}
            <div className="flex items-center w-full mb-[10px] mt-[10px]">
              <div
                style={{
                  background: "#e5e5e5",
                  height: 15,
                  borderRadius: 15,
                  flex: 1,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    background: MyColor.red,
                    height: 15,
                    width: `${ratioAmount}%`,
                    transition: "width 0.4s",
                  }}
                />
              </div>
            </div>
            {/* 金额和目标 */}
            <div className="flex items-end justify-between mt-[10px] mb-[6px]">
              <div className="flex flex-col items-start">
                <div
                  style={{
                    color: MyColor.red,
                    fontWeight: 600,
                    fontSize: 14,
                    fontStyle: "italic",
                    marginBottom: -2,
                  }}
                >
                  {t("my_record_detail.raised")}
                </div>
                <div
                  style={{
                    color: MyColor.red,
                    fontWeight: 600,
                    fontSize: 23,
                  }}
                >
                  HK$ {formatMoney(campaign?.raisedAmount)}
                </div>
              </div>
              <div className="flex flex-col items-end">
                <div
                  style={{
                    color: "#9b9b9b",
                    fontWeight: 600,
                    fontSize: 14,
                    fontStyle: "italic",
                    marginBottom: -2,
                  }}
                >
                  {t("my_record_detail.goal")}
                </div>
                <div
                  style={{
                    color: "#555",
                    fontWeight: 600,
                    fontSize: 23,
                  }}
                >
                  HK$ {formatMoney(campaign?.goalAmount)}
                </div>
              </div>
            </div>
            {/* 百分比与时间 */}
            <div
              className="flex items-center justify-between mt-[2px] mb-[10px]"
              style={{
                fontSize: 14,
                color: "#888",
                borderTop: "2px solid rgba(0,0,0,0.4)",
                paddingTop: 5,
              }}
            >
              <div className="text-[rgba(0,0,0,0.4)]">
                <span style={{ fontWeight: 600 }}>
                  {ratioAmount}%
                </span>{" "}
                {t("my_record_detail.funded")}
              </div>
              <div>
                <CampaignDeadline endDate={campaign?.expectedEndDate} />
              </div>
            </div>
            <ShareCampaign campaign={campaign} variant="record" />
          </div>
        </div>

        <div className="px-[18px]" style={{ position: 'relative' }}>
          {/* 感谢支持者 */}
          {campaignEarnedMedal(campaign) && (
            <div style={{ position: 'relative', height: 110 }}>
              <div style={{ position: 'absolute', left: 0, top: -10, right: 0, bottom: 0 }}>
                <div className="flex items-center justify-center h-[110px]" >
                  <img className="size-[130px]" src={goal_coin_big} alt="" />
                </div>
              </div>
            </div>
          )}

          {donationList.length ? (
            <div style={{ paddingTop: 10 }}>
              <div
                style={{
                  color: MyColor.blue,
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                {t("my_record_detail.fundraised_record")}
              </div>
              <div className="mt-[20px] flex-col space-y-[12px]">
                {donationList.map((item, idx) => (
                  <div
                    key={idx}
                    className="rounded-[22px] px-[20px] py-[18px]"
                    style={{
                      background: "#fff",
                      boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
                    }}
                  >
                    <div className="flex items-start justify-between gap-[18px]">
                      <div className="min-w-0 flex-1">
                        <div className="text-[17px] font-[700] leading-[1.15]" style={{ color: MyColor.blue }}>Name</div>
                        <div className="mt-[6px] text-[18px] font-[500] leading-[1.2]" style={{ color: MyColor.blue }}>
                          {getSupporterDisplayName(item)}
                        </div>
                      </div>
                      <div className="mt-[4px] h-[66px] w-[2px] bg-[rgba(15,68,145,0.85)]" />
                      <div className="min-w-[126px]">
                        <div className="text-[17px] font-[700] leading-[1.15]" style={{ color: MyColor.blue }}>Amount(HK$)</div>
                        <div className="mt-[6px] text-[18px] font-[500] leading-[1.2]" style={{ color: MyColor.blue }}>
                          {isAmountHidden(item)
                            ? t("campaign_detail.hidden_amount")
                            : formatMoney(item.amount)}
                        </div>
                      </div>
                    </div>
                    <div className="my-[16px] h-[2px] w-full bg-[rgba(15,68,145,0.85)]" />
                    <div className="text-[18px] leading-[1.45]" style={{ color: MyColor.blue }}>
                      <span className="text-[17px] font-[700]" style={{ color: MyColor.red }}>
                        {t("campaign_detail.supporting_message")}
                      </span>
                      <div className="mt-[4px]" style={{ minHeight: item.message ? undefined : 24 }}>
                        {item.message || ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : <div />}
        </div>
      </div>
    </PageBox>
  );
});

export default MyRecordDetail;
